import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { apiRequest, ApiError } from '../../utils/apiClient';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { getStoredThemeMode, resolveDark, setThemeMode } from '../../utils/theme';
import { latnLocale } from '../../utils/numberFormat';

type ThemeMode = 'light' | 'dark';

interface NotificationSettings {
  emailAlerts: boolean;
  priceDrops: boolean;
  newListings: boolean;
  savedSearches: boolean;
  marketingEmails: boolean;
}

interface PrivacySettings {
  showProfile: boolean;
  showFavorites: boolean;
  allowAnalytics: boolean;
}

export default function SettingsPage() {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const { user, session, signOut } = useAuth();
  const navigate = useNavigate();
  const isRTL = i18n.dir() === 'rtl';

  // Theme state — reflects the shared theme module's current resolved value
  // (this page only offers light/dark, so a stored "system" maps to whichever
  // it currently resolves to).
  const [theme, setTheme] = useState<ThemeMode>(() =>
    resolveDark(getStoredThemeMode()) ? 'dark' : 'light',
  );

  // Notification settings
  const [notifications, setNotifications] = useState<NotificationSettings>(() => {
    const saved = localStorage.getItem('notifications');
    return saved ? JSON.parse(saved) : {
      emailAlerts: true,
      priceDrops: true,
      newListings: true,
      savedSearches: false,
      marketingEmails: false,
    };
  });

  // Privacy settings
  const [privacy, setPrivacy] = useState<PrivacySettings>(() => {
    const saved = localStorage.getItem('privacy');
    return saved ? JSON.parse(saved) : {
      showProfile: true,
      showFavorites: false,
      allowAnalytics: true,
    };
  });

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('appearance');

  // Apply theme through the shared module (persists the canonical key, applies
  // the .dark class, and broadcasts to the navbar toggle + other consumers).
  useEffect(() => {
    setThemeMode(theme);
  }, [theme]);

  // Save notification settings
  useEffect(() => {
    localStorage.setItem('notifications', JSON.stringify(notifications));
  }, [notifications]);

  // Save privacy settings
  useEffect(() => {
    localStorage.setItem('privacy', JSON.stringify(privacy));
  }, [privacy]);

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  const handleNotificationChange = (key: keyof NotificationSettings) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handlePrivacyChange = (key: keyof PrivacySettings) => {
    setPrivacy(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveSettings = () => {
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleChangePassword = () => {
    // For now, show an alert that this feature is coming soon
    alert(t('settings:account.passwordChangeComingSoon', { defaultValue: 'Password change functionality is coming soon.' }));
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      t('settings:account.deleteConfirm', {
        defaultValue: 'Delete your account? Your personal data is anonymised within 30 days; records required by law (invoices, transactions) are retained. This cannot be undone.'
      })
    );
    if (!confirmed) return;
    try {
      await apiRequest('/auth/me', { method: 'DELETE', token: session?.token });
      alert(t('settings:account.deleteSuccess', { defaultValue: 'Your account has been deleted.' }));
      await signOut();
      navigate('/');
    } catch (error) {
      alert(error instanceof ApiError ? error.message : t('settings:account.deleteError', { defaultValue: 'Failed to delete account. Please try again or contact support.' }));
    }
  };

  const handleExportData = async () => {
    try {
      const data = await apiRequest<Record<string, unknown>>('/auth/me/export', { token: session?.token });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'safaqat24-my-data.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(error instanceof ApiError ? error.message : t('settings:privacy.exportError', { defaultValue: 'Failed to export your data. Please try again.' }));
    }
  };

  const sections = [
    { id: 'appearance', icon: 'ri-palette-line', label: t('settings:sections.appearance') },
    { id: 'notifications', icon: 'ri-notification-3-line', label: t('settings:sections.notifications') },
    { id: 'privacy', icon: 'ri-shield-check-line', label: t('settings:sections.privacy') },
    { id: 'account', icon: 'ri-user-settings-line', label: t('settings:sections.account') },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-xl bg-[rgba(0,77,64,0.15)]">
              <i className="ri-settings-4-line text-2xl text-[var(--pv-color-primary)]"></i>
            </div>
            <h1 className="text-2xl font-bold text-[var(--pv-color-primary)]">{t('settings:title')}</h1>
          </div>
          <p className="text-gray-600">{t('settings:subtitle')}</p>
        </div>

        {/* Success Message */}
        {saveSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 animate-fade-in">
            <i className="ri-check-line text-green-600 text-xl"></i>
            <span className="text-green-700">{t('settings:saveSuccess')}</span>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 sticky top-24">
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={[
                      'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200',
                      activeSection === section.id
                        ? 'bg-[rgba(0,77,64,0.12)] text-[var(--pv-color-primary)]'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                    ].join(' ')}
                  >
                    <i className={`${section.icon} text-lg`}></i>
                    <span>{section.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Appearance Section */}
              {activeSection === 'appearance' && (
                <div className="p-6 space-y-8">
                  <div>
                    <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] mb-1">
                      {t('settings:appearance.title')}
                    </h2>
                    <p className="text-sm text-gray-500">{t('settings:appearance.description')}</p>
                  </div>

                  {/* Theme Selection */}
                  <div className="space-y-4">
                    <label className="text-sm font-medium text-gray-700">
                      {t('settings:appearance.theme')}
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      {(['light', 'dark'] as ThemeMode[]).map((mode) => (
                        <button
                          key={mode}
                          onClick={() => setTheme(mode)}
                          className={[
                            'relative p-5 rounded-xl border-2 transition-all duration-200',
                            theme === mode
                              ? 'border-[var(--pv-color-primary)] bg-[rgba(0,77,64,0.04)]'
                              : 'border-gray-200 hover:border-gray-300 bg-white',
                          ].join(' ')}
                        >
                          <div className="flex flex-col items-center gap-3">
                            <div className={[
                              'w-14 h-14 rounded-xl flex items-center justify-center',
                              mode === 'light' ? 'bg-amber-100' : 'bg-gray-900',
                            ].join(' ')}>
                              <i className={[
                                'text-2xl',
                                mode === 'light' ? 'ri-sun-line text-yellow-600' : 'ri-moon-line text-gray-300',
                              ].join(' ')}></i>
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                              {t(`settings:appearance.${mode}`)}
                            </span>
                          </div>
                          {theme === mode && (
                            <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[var(--pv-color-primary)] flex items-center justify-center">
                              <i className="ri-check-line text-white text-xs"></i>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Language Selection */}
                  <div className="space-y-4">
                    <label className="text-sm font-medium text-gray-700">
                      {t('settings:appearance.language')}
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => handleLanguageChange('en')}
                        className={[
                          'relative p-4 rounded-xl border-2 transition-all duration-200',
                          i18n.language === 'en'
                            ? 'border-[var(--pv-color-primary)] bg-[rgba(0,77,64,0.04)]'
                            : 'border-gray-200 hover:border-gray-300 bg-white',
                        ].join(' ')}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">🇺🇸</span>
                          <div className={isRTL ? 'text-right' : 'text-left'}>
                            <span className="text-sm font-medium text-gray-700 block">English</span>
                            <span className="text-xs text-gray-500">United States</span>
                          </div>
                        </div>
                        {i18n.language === 'en' && (
                          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[var(--pv-color-primary)] flex items-center justify-center">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </button>
                      <button
                        onClick={() => handleLanguageChange('ar')}
                        className={[
                          'relative p-4 rounded-xl border-2 transition-all duration-200',
                          i18n.language === 'ar'
                            ? 'border-[var(--pv-color-primary)] bg-[rgba(0,77,64,0.04)]'
                            : 'border-gray-200 hover:border-gray-300 bg-white',
                        ].join(' ')}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">🇸🇦</span>
                          <div className={isRTL ? 'text-right' : 'text-left'}>
                            <span className="text-sm font-medium text-gray-700 block">العربية</span>
                            <span className="text-xs text-gray-500">المملكة العربية السعودية</span>
                          </div>
                        </div>
                        {i18n.language === 'ar' && (
                          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[var(--pv-color-primary)] flex items-center justify-center">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications Section */}
              {activeSection === 'notifications' && (
                <div className="p-6 space-y-8">
                  <div>
                    <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] mb-1">
                      {t('settings:notifications.title')}
                    </h2>
                    <p className="text-sm text-gray-500">{t('settings:notifications.description')}</p>
                  </div>

                  <div className="space-y-4">
                    {/* Email Alerts */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100">
                          <i className="ri-mail-line text-blue-600"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{t('settings:notifications.emailAlerts')}</p>
                          <p className="text-xs text-gray-500">{t('settings:notifications.emailAlertsDesc')}</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        enabled={notifications.emailAlerts}
                        onChange={() => handleNotificationChange('emailAlerts')}
                      />
                    </div>

                    {/* Price Drops */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-100">
                          <i className="ri-price-tag-3-line text-green-600"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{t('settings:notifications.priceDrops')}</p>
                          <p className="text-xs text-gray-500">{t('settings:notifications.priceDropsDesc')}</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        enabled={notifications.priceDrops}
                        onChange={() => handleNotificationChange('priceDrops')}
                      />
                    </div>

                    {/* New Listings */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[var(--pv-color-surface-sunken)]">
                          <i className="ri-home-4-line text-[#004D40]"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{t('settings:notifications.newListings')}</p>
                          <p className="text-xs text-gray-500">{t('settings:notifications.newListingsDesc')}</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        enabled={notifications.newListings}
                        onChange={() => handleNotificationChange('newListings')}
                      />
                    </div>

                    {/* Saved Searches */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-orange-100">
                          <i className="ri-search-line text-orange-600"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{t('settings:notifications.savedSearches')}</p>
                          <p className="text-xs text-gray-500">{t('settings:notifications.savedSearchesDesc')}</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        enabled={notifications.savedSearches}
                        onChange={() => handleNotificationChange('savedSearches')}
                      />
                    </div>

                    {/* Marketing Emails */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-pink-100">
                          <i className="ri-megaphone-line text-pink-600"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{t('settings:notifications.marketingEmails')}</p>
                          <p className="text-xs text-gray-500">{t('settings:notifications.marketingEmailsDesc')}</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        enabled={notifications.marketingEmails}
                        onChange={() => handleNotificationChange('marketingEmails')}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Privacy Section */}
              {activeSection === 'privacy' && (
                <div className="p-6 space-y-8">
                  <div>
                    <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] mb-1">
                      {t('settings:privacy.title')}
                    </h2>
                    <p className="text-sm text-gray-500">{t('settings:privacy.description')}</p>
                  </div>

                  <div className="space-y-4">
                    {/* Show Profile */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100">
                          <i className="ri-user-line text-blue-600"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{t('settings:privacy.showProfile')}</p>
                          <p className="text-xs text-gray-500">{t('settings:privacy.showProfileDesc')}</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        enabled={privacy.showProfile}
                        onChange={() => handlePrivacyChange('showProfile')}
                      />
                    </div>

                    {/* Show Favorites */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-red-100">
                          <i className="ri-heart-line text-red-600"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{t('settings:privacy.showFavorites')}</p>
                          <p className="text-xs text-gray-500">{t('settings:privacy.showFavoritesDesc')}</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        enabled={privacy.showFavorites}
                        onChange={() => handlePrivacyChange('showFavorites')}
                      />
                    </div>

                    {/* Allow Analytics */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-100">
                          <i className="ri-bar-chart-box-line text-green-600"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{t('settings:privacy.allowAnalytics')}</p>
                          <p className="text-xs text-gray-500">{t('settings:privacy.allowAnalyticsDesc')}</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        enabled={privacy.allowAnalytics}
                        onChange={() => handlePrivacyChange('allowAnalytics')}
                      />
                    </div>
                  </div>

                  {/* Data Management */}
                  <div className="pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">{t('settings:privacy.dataManagement')}</h3>
                    <div className="flex flex-wrap gap-3">
                      <Button variant="outline" size="sm" onClick={handleExportData}>
                        <i className="ri-download-line mr-2"></i>
                        {t('settings:privacy.downloadData')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleDeleteAccount} className="text-red-600 border-red-200 hover:bg-red-50">
                        <i className="ri-delete-bin-line mr-2"></i>
                        {t('settings:privacy.deleteData')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Account Section */}
              {activeSection === 'account' && (
                <div className="p-6 space-y-8">
                  <div>
                    <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] mb-1">
                      {t('settings:account.title')}
                    </h2>
                    <p className="text-sm text-gray-500">{t('settings:account.description')}</p>
                  </div>

                  {user ? (
                    <>
                      {/* User Info */}
                      <div className="p-4 bg-[rgba(0,77,64,0.08)] rounded-xl">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-full bg-[rgba(0,77,64,0.3)] flex items-center justify-center">
                            <i className="ri-user-line text-3xl text-[var(--pv-color-primary)]"></i>
                          </div>
                          <div>
                            <p className="text-lg font-semibold text-gray-900">
                              {user.firstName || user.lastName
                                ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                                : t('settings:account.noName')}
                            </p>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            {user.createdAt && (
                              <p className="text-xs text-gray-500 mt-1">
                                {t('settings:account.memberSince', {
                                  date: new Date(user.createdAt).toLocaleDateString(latnLocale(i18n.language === 'ar' ? 'ar-SA' : 'en-US'), {
                                    year: 'numeric',
                                    month: 'long',
                                  }),
                                })}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Account Actions */}
                      <div className="space-y-3">
                        <Link
                          to="/profile"
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-100">
                              <i className="ri-user-settings-line text-blue-600"></i>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{t('settings:account.editProfile')}</p>
                              <p className="text-xs text-gray-500">{t('settings:account.editProfileDesc')}</p>
                            </div>
                          </div>
                          <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-s-line text-gray-400`}></i>
                        </Link>

                        <button
                          onClick={handleChangePassword}
                          className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-yellow-100">
                              <i className="ri-lock-password-line text-yellow-600"></i>
                            </div>
                            <div className={isRTL ? 'text-right' : 'text-left'}>
                              <p className="text-sm font-medium text-gray-900">{t('settings:account.changePassword')}</p>
                              <p className="text-xs text-gray-500">{t('settings:account.changePasswordDesc')}</p>
                            </div>
                          </div>
                          <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-s-line text-gray-400`}></i>
                        </button>

                        <button
                          className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-[var(--pv-color-surface-sunken)]">
                              <i className="ri-smartphone-line text-[#004D40]"></i>
                            </div>
                            <div className={isRTL ? 'text-right' : 'text-left'}>
                              <p className="text-sm font-medium text-gray-900">{t('settings:account.twoFactor')}</p>
                              <p className="text-xs text-gray-500">{t('settings:account.twoFactorDesc')}</p>
                            </div>
                          </div>
                          <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded-full">
                            {t('settings:account.comingSoon')}
                          </span>
                        </button>
                      </div>

                      {/* Danger Zone */}
                      <div className="pt-6 border-t border-gray-200">
                        <h3 className="text-sm font-medium text-red-600 mb-4">{t('settings:account.dangerZone')}</h3>
                        <div className="flex flex-wrap gap-3">
                          <Button
                            variant="outline"
                            onClick={handleSignOut}
                            className="text-gray-600 border-gray-300 hover:bg-gray-50"
                          >
                            <i className="ri-logout-circle-line mr-2"></i>
                            {t('settings:account.signOut')}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleDeleteAccount}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <i className="ri-delete-bin-line mr-2"></i>
                            {t('settings:account.deleteAccount')}
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                        <i className="ri-user-line text-4xl text-gray-400"></i>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('settings:account.notSignedIn')}</h3>
                      <p className="text-sm text-gray-500 mb-6">{t('settings:account.signInToManage')}</p>
                      <Link to="/signin">
                        <Button>
                          <i className="ri-login-circle-line mr-2"></i>
                          {t('common:auth.signIn')}
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* Save Button */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                <Button onClick={handleSaveSettings}>
                  <i className="ri-save-line mr-2"></i>
                  {t('settings:saveSettings')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

// Toggle Switch Component
function ToggleSwitch({ enabled, onChange }: { enabled: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={[
        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)] focus:ring-offset-2',
        enabled ? 'bg-[var(--pv-color-primary)]' : 'bg-gray-200',
      ].join(' ')}
      role="switch"
      aria-checked={enabled}
    >
      <span
        className={[
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          enabled ? 'translate-x-5' : 'translate-x-0',
        ].join(' ')}
      />
    </button>
  );
}
